// src/utils/routeUtils.js
export const EARTH_RADIUS = 6371; // km

/** Haversine – straight-line distance (km) */
export const CHARGING_STATION_RADIUS_METERS = 25

export const CHARGING_STATION_COORDS = {
  lat: 13.19519911007788,
  lng: 77.7063383111108,
};

export const isChargingStation = (stopName) => {
  return /charging\s*station/i.test(stopName || "");
};

/** Haversine – straight-line distance (km) */
export const haversineKm = (p1, p2) => {
  const valid = p => p && [p.lat ?? p.latitude, p.lng ?? p.longitude]
    .every(value => value != null && Number.isFinite(Number(value)));
  if (!valid(p1) || !valid(p2)) return null;
  const toRad = v => (v * Math.PI) / 180;
  const φ1 = toRad(p1.lat ?? p1.latitude);
  const φ2 = toRad(p2.lat ?? p2.latitude);
  const Δφ = toRad((p2.lat ?? p2.latitude) - (p1.lat ?? p1.latitude));
  const Δλ = toRad((p2.lng ?? p2.longitude) - (p1.lng ?? p1.longitude));

  const a = Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS * c;
};

/** Find index of the *closest* point on the polyline */
export const nearestIndex = (point, polyline) => {
  let best = -1, min = Infinity;
  polyline.forEach((c, i) => {
    const d = haversineKm(point, c);
    if (d !== null && d < min) { min = d; best = i; }
  });
  return best;
};

/** Sum distance along polyline from idxA → idxB (km) */
export const segmentDistance = (polyline, iA, iB) => {
  if (iA === -1 || iB === -1) return null;
  const [from, to] = iA < iB ? [iA, iB] : [iB, iA];
  let total = 0;
  for (let i = from; i < to; i++) {
    const d = haversineKm(polyline[i], polyline[i + 1]);
    if (d === null) return null;
    total += d;
  }
  return total;
};

/**
 * Build a lookup table:
 *   distances[routeId][fromId][toId] = km along the route
 */
export const buildStopDistances = (routes, stops) => {
  const table = {};

  routes.forEach(route => {
    if (!route?.routeCoordinates?.length) return;
    const coords = [...route.routeCoordinates]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // source / destination points (if they exist)
    const src = coords[0];
    const dst = coords[coords.length - 1];

    const points = [
      { id: src.id ?? "source", latitude: src.latitude ?? src.lat, longitude: src.longitude ?? src.lng },
      ...stops
        .filter(s => s.latitude && s.longitude)
        .map(s => ({ id: s.id, latitude: +s.latitude, longitude: +s.longitude })),
      { id: dst.id ?? "destination", latitude: dst.latitude ?? dst.lat, longitude: dst.longitude ?? dst.lng },
    ];

    const routeTable = {};
    points.forEach(p1 => {
      const key = `${route.id}_${p1.id}`;
      routeTable[key] = {};
      points.forEach(p2 => {
        if (p1.id === p2.id) return;
        const i1 = nearestIndex(p1, coords);
        const i2 = nearestIndex(p2, coords);
        if (i1 === -1 || i2 === -1) return;
        const d = segmentDistance(coords, i1, i2);
        if (d !== null) routeTable[key][p2.id] = +d.toFixed(3);
      });
    });
    table[route.id] = routeTable;
  });

  return table;
};
// A missing status or GPS position cannot establish that a bus is in service.
export const isVehicleInService = (status, live, chargingStations = []) => {
  if (status !== "ACTIVE" || haversineKm(live, live) === null) return false;
  return ![CHARGING_STATION_COORDS, ...chargingStations].some(station => {
    const distance = haversineKm(live, station);
    return distance !== null && distance * 1000 <= CHARGING_STATION_RADIUS_METERS;
  });
};
